// Phase 8 Plan 03 Task 1 — per-template walker for the `option-set` template.
//
// Pure TS — no React, no DOM, no Date.now, no Math.random. Server-friendly:
// Phase 10 AI prompt-gen + Phase 13 SSR re-import from here.
// Hand-written per RESEARCH §"Pattern 2 Option A" — avoids Zod 4 type-erasure
// under D-15 paranoid-strict tsconfig (isolatedDeclarations strips the inner
// schema type from `z.ZodType<OptionSetConfig>`-annotated exports).
//
// configSchema source mirrored verbatim (../../schemas/widget-templates/option-set.ts):
//   options: ReadonlyArray<{ id: string; label: string; categoryIds?; tierId? }>
//   cardinality: { min: number; max?: number }
//
// Walker output shape: kind:'group' root at `configPath`, with two children:
//   - kind:'array' options + nested kind:'group' option item (id/label/tierId/categoryIds)
//   - kind:'group' cardinality with two leaves (min, max)

import type { FieldDescriptor } from "../descriptors.js";

export function walkOptionSetConfig(configPath: string): FieldDescriptor {
  return {
    kind: "group",
    name: "config",
    path: configPath,
    children: [
      {
        kind: "array",
        name: "options",
        path: `${configPath}.options`,
        itemDescriptor: {
          kind: "group",
          name: "option",
          path: `${configPath}.options.*`,
          children: [
            {
              kind: "leaf",
              name: "id",
              path: `${configPath}.options.*.id`,
              type: "string",
              required: true,
            },
            {
              kind: "leaf",
              name: "label",
              path: `${configPath}.options.*.label`,
              type: "string",
              required: true,
            },
            {
              // tierId: optional (Phase 7 D-07 — references RankedTier.id when paired).
              kind: "leaf",
              name: "tierId",
              path: `${configPath}.options.*.tierId`,
              type: "string",
              required: false,
            },
            {
              // categoryIds: optional ReadonlyArray<string> — a string-array per option.
              kind: "array",
              name: "categoryIds",
              path: `${configPath}.options.*.categoryIds`,
              itemDescriptor: {
                kind: "leaf",
                name: "categoryId",
                path: `${configPath}.options.*.categoryIds.*`,
                type: "string",
                required: true,
              },
            },
          ],
        },
      },
      {
        kind: "group",
        name: "cardinality",
        path: `${configPath}.cardinality`,
        children: [
          {
            kind: "leaf",
            name: "min",
            path: `${configPath}.cardinality.min`,
            type: "number",
            required: true,
          },
          {
            kind: "leaf",
            name: "max",
            path: `${configPath}.cardinality.max`,
            type: "number",
            required: false,
          },
        ],
      },
    ],
  };
}
